<template>
  <div class="p-6 mt-16 max-w-7xl mx-auto">
    <div class="flex items-center justify-between mb-4">
      <h1 class="text-2xl font-bold text-gray-900">Admin · Channel Tools</h1>
      <RouterLink to="/admin/reports" class="text-sm text-indigo-700 hover:underline">← Back to Admin Reports</RouterLink>
    </div>

    <!-- Tabs -->
    <div class="mb-4 flex items-center gap-2 border-b border-gray-200">
      <button @click="activeTab = 'summaries'" :class="tabClass('summaries')">Per Channel Summaries</button>
      <button @click="activeTab = 'followers'" :class="tabClass('followers')">Channel Followers</button>
      <button @click="activeTab = 'missing'" :class="tabClass('missing')">Missing Summaries</button>
      <button @click="activeTab = 'users'" :class="tabClass('users')">Users</button>
      <button @click="activeTab = 'api-usage'" :class="tabClass('api-usage')">API Usage</button>
    </div>

    <!-- Tab panels -->
    <div v-if="activeTab === 'summaries'">
      <div class="flex items-center justify-between mb-3">
        <h2 class="text-lg font-semibold text-gray-900">Channel Summaries</h2>
        <div class="flex items-center gap-2">
          <label class="text-sm text-gray-600 flex items-center gap-2">
            Per channel
            <input type="number" v-model.number="perChannelLimit" min="1" max="200" class="w-20 px-2 py-1 border rounded-lg text-sm" />
          </label>
          <button @click="loadChannelSummaries" :disabled="loadingSummaries" class="px-3 py-1.5 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">Load</button>
        </div>
      </div>

      <div v-if="loadingSummaries" class="p-6 bg-white rounded-xl shadow border border-gray-100 text-sm text-gray-600">Loading...</div>

      <div v-else class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <div v-for="g in channelSummaries" :key="g.channel_id" class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div class="p-4 border-b border-gray-100 flex items-start justify-between">
            <div>
              <div class="text-sm text-gray-500">{{ g.channel_id }}</div>
              <div class="text-base font-semibold text-gray-900">{{ g.channel_title || 'Untitled Channel' }}</div>
            </div>
            <span class="inline-flex items-center text-xs font-medium px-2 py-1 rounded-full bg-indigo-50 text-indigo-700">{{ g.items?.length || 0 }} videos</span>
          </div>
          <ul class="divide-y divide-gray-100">
            <li v-for="it in g.items" :key="it.video_id" class="p-3 hover:bg-gray-50">
              <div class="min-w-0">
                <RouterLink :to="createVideoUrl(it.title || '', it.video_id, 'tr')" class="text-sm font-mono text-indigo-700 hover:underline">
                  {{ it.video_id }}
                </RouterLink>
                <div class="text-sm text-gray-700 truncate">{{ it.title || 'No title' }}</div>
                <div v-if="it.created_at" class="text-xs text-gray-500 mt-0.5">Summary added: {{ new Date(it.created_at).toLocaleString() }}</div>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div v-else-if="activeTab === 'followers'">
      <div class="flex items-center justify-between mb-3">
        <h2 class="text-lg font-semibold text-gray-900">Channel Followers</h2>
        <div class="flex items-center gap-3">
          <label class="text-sm text-gray-600 flex items-center gap-2">
            <input type="checkbox" v-model="includeEmails" /> include emails
          </label>
          <label class="text-sm text-gray-600 flex items-center gap-2">
            Email limit
            <input type="number" v-model.number="emailLimit" min="1" max="100" class="w-20 px-2 py-1 border rounded-lg text-sm" />
          </label>
          <button @click="loadChannelFollowers" :disabled="loadingFollowers" class="px-3 py-1.5 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">Load</button>
        </div>
      </div>

      <div v-if="loadingFollowers" class="p-6 bg-white rounded-xl shadow border border-gray-100 text-sm text-gray-600">Loading...</div>

      <div v-else class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <div v-for="it in channelFollowers" :key="it.channel_id" class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div class="p-4 border-b border-gray-100 flex items-start justify-between">
            <div>
              <div class="text-sm text-gray-500">{{ it.channel_id }}</div>
              <div class="text-base font-semibold text-gray-900">{{ it.title || 'Untitled Channel' }}</div>
            </div>
            <span class="inline-flex items-center text-xs font-medium px-2 py-1 rounded-full bg-emerald-50 text-emerald-700">{{ it.follower_count }} followers</span>
          </div>
          <div class="p-3 text-sm text-gray-700" v-if="includeEmails">
            <div class="font-medium mb-1">Emails</div>
            <div class="text-gray-600 whitespace-pre-wrap break-words">{{ (it.follower_emails || []).join(', ') || '—' }}</div>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="activeTab === 'missing'">
      <div class="flex items-center justify-between mb-3">
        <h2 class="text-lg font-semibold text-gray-900">Missing Summaries</h2>
        <div class="flex items-center gap-3">
          <label class="text-sm text-gray-600 flex items-center gap-2">
            Language
            <select v-model="missingLang" class="px-2 py-1 border rounded-lg text-sm">
              <option value="tr">tr</option>
              <option value="en">en</option>
            </select>
          </label>
          <button @click="loadMissingSummaries" :disabled="loadingMissing" class="px-3 py-1.5 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">Load</button>
        </div>
      </div>

      <div v-if="loadingMissing" class="p-6 bg-white rounded-xl shadow border border-gray-100 text-sm text-gray-600">Loading...</div>

      <div v-else class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <div v-for="g in missingSummaries" :key="g.channel_id" class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div class="p-4 border-b border-gray-100 flex items-start justify-between">
            <div>
              <div class="text-sm text-gray-500">{{ g.channel_id }}</div>
              <div class="text-base font-semibold text-gray-900">{{ g.channel_title || 'Untitled Channel' }}</div>
            </div>
            <span class="inline-flex items-center text-xs font-medium px-2 py-1 rounded-full bg-rose-50 text-rose-700">{{ g.items?.length || 0 }} missing</span>
          </div>
          <ul class="divide-y divide-gray-100">
            <li v-for="it in g.items" :key="it.video_id" class="p-3 hover:bg-gray-50">
              <div class="min-w-0">
                <span class="text-sm font-mono text-gray-700">{{ it.video_id }}</span>
                <div class="text-sm text-gray-700 truncate">{{ it.title || 'No title' }}</div>
                <div class="text-xs text-gray-500 mt-0.5">Added: {{ it.added_at ? new Date(it.added_at).toLocaleString() : '—' }} • Published: {{ it.published_at ? new Date(it.published_at).toLocaleString() : '—' }}</div>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div v-else>
      <div class="flex items-center justify-between mb-3">
        <h2 class="text-lg font-semibold text-gray-900">Users</h2>
        <div class="text-sm text-gray-600">Profiles total: {{ usersProfilesCount }}</div>
      </div>
      <div class="p-4 bg-white rounded-xl shadow-sm border border-gray-100">
        <div class="text-sm text-gray-600 mb-3">Showing {{ users.length }} users</div>
        <div class="overflow-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="text-left text-gray-600">
                <th class="py-2 pr-4">ID</th>
                <th class="py-2 pr-4">Email</th>
                <th class="py-2 pr-4">Name</th>
                <th class="py-2 pr-4">Provider</th>
                <th class="py-2 pr-4">Created</th>
                <th class="py-2 pr-4">Last sign-in</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="u in users" :key="u.id" class="border-t border-gray-100">
                <td class="py-2 pr-4 font-mono text-xs">{{ u.id }}</td>
                <td class="py-2 pr-4">{{ u.email || '—' }}</td>
                <td class="py-2 pr-4">{{ u.name || '—' }}</td>
                <td class="py-2 pr-4">{{ u.provider || '—' }}</td>
                <td class="py-2 pr-4">{{ u.created_at ? new Date(u.created_at).toLocaleString() : '—' }}</td>
                <td class="py-2 pr-4">{{ u.last_sign_in_at ? new Date(u.last_sign_in_at).toLocaleString() : '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
  
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import api from '@/services/apiService'
import { createVideoUrl } from '@/utils/helpers'

const activeTab = ref<'summaries' | 'followers' | 'missing' | 'users'>('summaries')

function tabClass(tab: 'summaries' | 'followers') {
  return [
    'px-3 py-2 text-sm font-medium -mb-px',
    activeTab.value === tab
      ? 'border-b-2 border-indigo-600 text-indigo-700'
      : 'text-gray-500 hover:text-gray-700'
  ]
}

// Summaries
const perChannelLimit = ref<number>(20)
const loadingSummaries = ref<boolean>(false)
const channelSummaries = ref<Array<{ channel_id:string; channel_title?:string|null; items: Array<{ video_id: string; title?: string|null }> }>>([])

async function loadChannelSummaries() {
  loadingSummaries.value = true
  try {
    const res = await api.get(`/api/admin/channel-summaries?perChannelLimit=${perChannelLimit.value}`)
    channelSummaries.value = res?.items || []
  } catch (e) {
    console.error('Load channel summaries error', e)
    channelSummaries.value = []
  } finally {
    loadingSummaries.value = false
  }
}

// Followers
const includeEmails = ref<boolean>(true)
const emailLimit = ref<number>(100)
const loadingFollowers = ref<boolean>(false)
const channelFollowers = ref<Array<{channel_id:string; title:string; follower_count:number; follower_emails?:string[]}>>([])
const followersLoaded = ref<boolean>(false)

async function loadChannelFollowers() {
  loadingFollowers.value = true
  try {
    const qs = `?includeEmails=${includeEmails.value}&emailLimit=${emailLimit.value}`
    const res = await api.get(`/api/admin/channel-followers${qs}`)
    channelFollowers.value = res?.items || []
    followersLoaded.value = true
  } catch (e) {
    console.error('Load channel followers error', e)
    channelFollowers.value = []
  } finally {
    loadingFollowers.value = false
  }
}

// initial load
loadChannelSummaries()

// Auto-load followers when tab opened
watch(activeTab, (tab) => {
  if (tab === 'followers' && !followersLoaded.value && !loadingFollowers.value) {
    loadChannelFollowers()
  }
})

// Auto-refresh followers list when options change and tab is active
watch([includeEmails, emailLimit], () => {
  if (activeTab.value === 'followers' && !loadingFollowers.value) {
    loadChannelFollowers()
  }
})

// Missing summaries
const missingLang = ref<'tr' | 'en'>('tr')
const loadingMissing = ref<boolean>(false)
const missingSummaries = ref<Array<{ channel_id: string; channel_title?: string|null; items: Array<{ video_id: string; title?: string|null; added_at?: string; published_at?: string|null }> }>>([])

async function loadMissingSummaries() {
  loadingMissing.value = true
  try {
    const res = await api.get(`/api/admin/missing-summaries?lang=${missingLang.value}`)
    missingSummaries.value = res?.items || []
  } catch (e) {
    console.error('Load missing summaries error', e)
    missingSummaries.value = []
  } finally {
    loadingMissing.value = false
  }
}

// Auto-load when tab activated
watch(activeTab, (tab) => {
  if (tab === 'missing' && !loadingMissing.value && missingSummaries.value.length === 0) {
    loadMissingSummaries()
  }
})

// Users
const users = ref<Array<{ id: string; email?: string; created_at?: string; last_sign_in_at?: string; provider?: string; name?: string }>>([])
const usersProfilesCount = ref<number>(0)
async function loadUsers() {
  try {
    const res = await api.get('/api/admin/users')
    users.value = res?.items || []
    usersProfilesCount.value = res?.profilesCount || 0
  } catch (e) {
    console.error('Load users error', e)
    users.value = []
    usersProfilesCount.value = 0
  }
}

watch(activeTab, (tab) => {
  if (tab === 'users' && users.value.length === 0) {
    loadUsers()
  }
})
</script>

<style scoped>
</style>


